import { useEffect, useMemo, useState } from "react";

export type Mesa28AnalyticsPeriod = "today" | "7d" | "30d" | "all";

interface AnalyticsPayload {
  period: Mesa28AnalyticsPeriod;
  timeZone: string;
  summary: {
    pageViews: number;
    visitors: number;
    repeatVisitors: number;
    whatsappClicks: number;
    contactRatePct: number;
    mobileSharePct: number;
  };
  daily: readonly { day: string; pageViews: number; visitors: number; whatsappClicks: number }[];
  devices: readonly { device: "mobile" | "tablet" | "desktop"; count: number }[];
  referrers: readonly { referrer: string; count: number }[];
  historicalEstimated: boolean;
}

const PERIODS: readonly { id: Mesa28AnalyticsPeriod; label: string }[] = [
  { id: "today", label: "Hoy" },
  { id: "7d", label: "7 días" },
  { id: "30d", label: "30 días" },
  { id: "all", label: "Todo" },
];

const DEVICE_LABEL: Record<string, string> = {
  mobile: "Móvil",
  tablet: "Tablet",
  desktop: "Desktop",
};

function percent(value: number): string {
  return `${new Intl.NumberFormat("es-AR", { maximumFractionDigits: 1 }).format(value)}%`;
}

function compactDay(day: string): string {
  const parsed = new Date(`${day}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return day;
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short" }).format(parsed);
}

export function Mesa28Analytics({ organizationSlug, appKey }: { organizationSlug: string; appKey: string }) {
  const [period, setPeriod] = useState<Mesa28AnalyticsPeriod>("30d");
  const [data, setData] = useState<AnalyticsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    fetch(`/api/v1/custom/apps/${encodeURIComponent(appKey)}/analytics?period=${period}`, {
      method: "GET",
      credentials: "include",
      signal: controller.signal,
      headers: { "x-clickdialog-organization-slug": organizationSlug },
    })
      .then(async (response) => {
        const body = await response.json().catch(() => ({})) as { analytics?: AnalyticsPayload; error?: string };
        if (!response.ok || !body.analytics) throw new Error(body.error ?? "No se pudieron cargar las estadísticas.");
        setData(body.analytics);
      })
      .catch((reason) => {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        setError(reason instanceof Error ? reason.message : "No se pudieron cargar las estadísticas.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [organizationSlug, appKey, period]);

  const maxDaily = useMemo(
    () => Math.max(1, ...(data?.daily.map((row) => row.pageViews) ?? [1])),
    [data],
  );

  if (loading && data === null) {
    return <section className="mesa-panel mesa-analytics-state">Cargando estadísticas…</section>;
  }

  if (error && data === null) {
    return <section className="mesa-panel mesa-analytics-state error"><strong>No se pudieron cargar las estadísticas.</strong><span>{error}</span></section>;
  }

  const analytics = data!;
  return (
    <section className="mesa-panel mesa-analytics">
      <div className="mesa-analytics-head">
        <div>
          <h2>Estadísticas</h2>
          <p>Actividad de la web pública y contactos iniciados desde WhatsApp.</p>
        </div>
        <div className="mesa-periods" aria-label="Periodo de estadísticas">
          {PERIODS.map((item) => (
            <button key={item.id} className={period === item.id ? "active" : ""} onClick={() => setPeriod(item.id)} disabled={loading}>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="mesa-analytics-inline-error">{error}</div>}
      {analytics.historicalEstimated && <div className="mesa-analytics-history-note">Parte del histórico anterior a Analytics V1 proviene de logs del gateway y sus visitantes únicos son una estimación.</div>}

      <div className="mesa-kpis">
        <article><span>Visitas</span><strong>{analytics.summary.pageViews}</strong><small>Cargas humanas de la landing</small></article>
        <article><span>Visitantes</span><strong>{analytics.summary.visitors}</strong><small>Navegadores anónimos distintos</small></article>
        <article><span>Clics WhatsApp</span><strong>{analytics.summary.whatsappClicks}</strong><small>Contactos iniciados</small></article>
        <article><span>Tasa de contacto</span><strong>{percent(analytics.summary.contactRatePct)}</strong><small>Clics / visitas</small></article>
      </div>

      <div className="mesa-analytics-grid">
        <article className="mesa-analytics-card mesa-daily-card">
          <div className="mesa-analytics-card-head"><div><h3>Actividad por día</h3><p>{analytics.summary.repeatVisitors} visitantes recurrentes</p></div></div>
          {analytics.daily.length === 0 ? <div className="mesa-analytics-empty">Todavía no hay actividad en este periodo.</div> : (
            <div className="mesa-daily-list">
              {analytics.daily.map((row) => (
                <div className="mesa-daily-row" key={row.day}>
                  <time dateTime={row.day}>{compactDay(row.day)}</time>
                  <div className="mesa-daily-track"><span style={{ width: `${Math.max(4, (row.pageViews / maxDaily) * 100)}%` }} /></div>
                  <strong>{row.pageViews}</strong>
                  <small>{row.whatsappClicks} WA</small>
                </div>
              ))}
            </div>
          )}
        </article>

        <article className="mesa-analytics-card">
          <div className="mesa-analytics-card-head"><div><h3>Dispositivos</h3><p>{percent(analytics.summary.mobileSharePct)} móvil/tablet</p></div></div>
          <div className="mesa-breakdown-list">
            {analytics.devices.length === 0 ? <div className="mesa-analytics-empty">Sin datos todavía.</div> : analytics.devices.map((row) => {
              const share = analytics.summary.pageViews === 0 ? 0 : (row.count / analytics.summary.pageViews) * 100;
              return <div key={row.device}><span>{DEVICE_LABEL[row.device] ?? row.device}</span><strong>{row.count}</strong><small>{percent(share)}</small></div>;
            })}
          </div>
        </article>

        <article className="mesa-analytics-card">
          <div className="mesa-analytics-card-head"><div><h3>Origen</h3><p>Referrer cuando el navegador lo informa</p></div></div>
          <div className="mesa-breakdown-list">
            {analytics.referrers.length === 0 ? <div className="mesa-analytics-empty">Sin datos todavía.</div> : analytics.referrers.map((row) => (
              <div key={row.referrer}><span>{row.referrer === "direct" ? "Directo / QR" : row.referrer}</span><strong>{row.count}</strong></div>
            ))}
          </div>
        </article>
      </div>

      <p className="mesa-analytics-privacy">Medición first-party de ClickDialog. No usa Google Analytics, Meta Pixel ni guarda IPs en claro.</p>
    </section>
  );
}
